<template>
  <div class="activity-list">
    <div
      v-for="(activity, index) in activities"
      :key="activity.id || index"
      class="activity-container"
      :style="{
        justifyContent: quoteGenerated ? 'start' : 'space-between',
      }"
    >
      <!-- Activity Header -->
      <div class="activity-header">
        <div class="activity-title-section">
          <h3 class="activity-title" :title="activity.title">
            {{ activity.title }}
          </h3>
          <div class="drag-icon" v-if="!quoteGenerated">
            <img src="/images/icon/drag.svg" alt="Drag" class="drag-icon-img" />
          </div>
        </div>
        <div class="activity-divider"></div>
      </div>

      <!-- Activity Details -->
      <div class="activity-details">
        <div class="detail-row">
          <span class="detail-label">Grade:</span>
          <span class="detail-value">{{ formatGrade(activity.grade) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Focus:</span>
          <span class="detail-value">{{ activity.focus || "-" }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Number of projects:</span>
          <span class="detail-value"
            >{{ activity.projects || 0 }} projects</span
          >
        </div>
        <div
          v-if="locale !== 'SG'"
          class="detail-row poster-row"
          :class="{ 'poster-row--no': !activity.includingPoster }"
        >
          <span class="detail-label">Poster:</span>
          <span class="detail-value">{{
            activity.includingPoster ? "Yes" : "No"
          }}</span>
        </div>
      </div>

      <!-- Edit Button - Desktop Menu -->
      <v-menu
        z-index="10000000"
        v-if="!quoteGenerated && cartType !== 'signature' && !isMobile"
      >
        <template #activator="{ props: menuProps }">
          <ActionButton
            class="activity-edit-btn"
            variant="secondary"
            full-width=""
            @click="editActivity(activity, index)"
            v-bind="menuProps"
            @click.stop
          >
            <template #icon-left>
              <img src="/images/icon/edit.svg" alt="Edit" class="edit-icon" />
            </template>
            <span class="edit-text">Edit</span>
          </ActionButton>
        </template>
        <v-list class="activity-menu-list">
          <!-- Duplicate Activity -->
          <v-list-item
            class="activity-menu-item"
            @click="openDuplicateModal(activity, index)"
          >
            <v-list-item-title>
              <div class="activity-action-button">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 16H6C4.89543 16 4 15.1046 4 14V6C4 4.89543 4.89543 4 6 4H14C15.1046 4 16 4.89543 16 6V8M10 20H18C19.1046 20 20 19.1046 20 18V10C20 8.89543 19.1046 8 18 8H10C8.89543 8 8 8.89543 8 10V18C8 19.1046 8.89543 20 10 20Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Duplicate Activity</span>
              </div>
            </v-list-item-title>
          </v-list-item>

          <!-- Move to another Cart -->
          <v-list-item
            class="activity-menu-item"
            @click="openMoveModal(activity, index)"
            v-if="locale !== 'SG'"
          >
            <v-list-item-title>
              <div class="activity-action-button">
                <svg
                  width="20"
                  height="11"
                  viewBox="0 0 20 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 1.49756L19 5.49756M19 5.49756L15 9.49756M19 5.49756L1 5.49756"
                    stroke="#3B4648"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span>Move to another Cart</span>
              </div>
            </v-list-item-title>
          </v-list-item>

          <!-- Edit Activity -->
          <v-list-item
            class="activity-menu-item"
            @click="openEditModal(activity, index)"
          >
            <v-list-item-title>
              <div class="activity-action-button">
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.2322 3.72979L16.7677 7.26533M14.7322 2.22979C15.7085 1.25348 17.2914 1.25348 18.2677 2.22979C19.244 3.2061 19.244 4.78901 18.2677 5.76533L4.5 19.533H1V15.962L14.7322 2.22979Z"
                    stroke="#3B4648"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span>Edit Activity</span>
              </div>
            </v-list-item-title>
          </v-list-item>

          <!-- Convert to Single Activity -->
          <v-list-item
            class="activity-menu-item"
            @click="openConvertModal(activity, index)"
            v-if="locale !== 'SG'"
          >
            <v-list-item-title>
              <div class="activity-action-button">
                <svg
                  width="18"
                  height="21"
                  viewBox="0 0 18 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 5.49756V15.4976C1 17.7067 4.58172 19.4976 9 19.4976C13.4183 19.4976 17 17.7067 17 15.4976V5.49756M1 5.49756C1 7.7067 4.58172 9.49756 9 9.49756C13.4183 9.49756 17 7.7067 17 5.49756M1 5.49756C1 3.28842 4.58172 1.49756 9 1.49756C13.4183 1.49756 17 3.28842 17 5.49756M17 10.4976C17 12.7067 13.4183 14.4976 9 14.4976C4.58172 14.4976 1 12.7067 1 10.4976"
                    stroke="#3B4648"
                    stroke-width="2"
                  />
                </svg>

                <span>Convert to Single Activity</span>
              </div>
            </v-list-item-title>
          </v-list-item>

          <!-- Delete Activity -->
          <v-list-item
            class="activity-menu-item"
            @click="openDeleteModal(activity, index)"
          >
            <v-list-item-title>
              <div class="activity-action-button">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6H5H21"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10 11V17"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14 11V17"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Delete Activity</span>
              </div>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Edit Button - Mobile Bottom Sheet Trigger -->
      <ActionButton
        v-if="!quoteGenerated && cartType !== 'signature' && isMobile"
        class="activity-edit-btn"
        variant="secondary"
        full-width=""
        @click="openBottomSheet(activity, index)"
        @click.stop
      >
        <template #icon-left>
          <img src="/images/icon/edit.svg" alt="Edit" class="edit-icon" />
        </template>
        <span class="edit-text">Edit</span>
      </ActionButton>

      <!-- Edit Button for Signature Cart (without menu) -->
      <ActionButton
        v-if="!quoteGenerated && cartType === 'signature'"
        class="activity-edit-btn"
        variant="secondary"
        full-width=""
        @click="editActivity(activity, index)"
        @click.stop
      >
        <template #icon-left>
          <img src="/images/icon/edit.svg" alt="Edit" class="edit-icon" />
        </template>
        <span class="edit-text">Edit</span>
      </ActionButton>
    </div>
    <div
      v-if="
        (!activities || activities.length < maxActivities) &&
        !quoteGenerated &&
        cartType === 'custom'
      "
      class="add-activity-container"
      :class="getAddActivityPosition(activities?.length || 0)"
      @click="addActivityToCart"
    >
      <button class="add-activity-btn">
        <span class="add-activity-text">Add Activity</span>
        <svg
          width="43"
          height="43"
          viewBox="0 0 43 43"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="add-activity-icon"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M21.5 8.95833C22.3837 8.95833 23.1 9.67467 23.1 10.5583V19.3333H31.875C32.7587 19.3333 33.475 20.0497 33.475 20.9333C33.475 21.817 32.7587 22.5333 31.875 22.5333H23.1V31.3083C23.1 32.192 22.3837 32.9083 21.5 32.9083C20.6163 32.9083 19.9 32.192 19.9 31.3083V22.5333H11.125C10.2413 22.5333 9.525 21.817 9.525 20.9333C9.525 20.0497 10.2413 19.3333 11.125 19.3333H19.9V10.5583C19.9 9.67467 20.6163 8.95833 21.5 8.95833Z"
            fill="#003D6A"
          />
        </svg>
      </button>
      <p class="add-activity-subtitle">Choose from catalog</p>
    </div>

    <!-- Move Activity Modal -->
    <MoveActivityModal
      v-model="showMoveModal"
      :available-carts="availableCarts"
      :current-cart-id="currentCartId"
      :activity-data="selectedActivity"
      :loading="isCartLoading"
      @move="handleMoveActivity"
      @cancel="closeMoveModal"
    />

    <!-- Duplicate Activity Modal -->
    <DuplicateModal
      v-model="showDuplicateModal"
      :title="duplicateModalTitle"
      :description="duplicateModalDescription"
      :confirm-text="duplicateModalConfirmText"
      cancel-text="Back"
      :loading="isCartLoading"
      @confirm="handleDuplicateConfirm"
      @cancel="handleDuplicateCancel"
    />

    <!-- Delete Activity Modal -->
    <DeleteModal
      v-model="showDeleteModal"
      title="Delete Activity"
      description="Are you sure want to delete this activity?"
      confirm-text="Delete Activity"
      cancel-text="Back"
      :loading="isCartLoading"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />

    <!-- Convert to Single Activity Modal -->
    <ConfirmationModal
      v-model="showConvertModal"
      title="Convert to Single Activity?"
      description="This activity will be converted to a single activity and priced individually, outside of the Makercart bundle. Are you sure you want to continue?"
      confirm-text="Convert This Activity"
      cancel-text="No, I Want To Keep It"
      :loading="isCartLoading"
      @confirm="handleConvertConfirm"
      @cancel="handleConvertCancel"
      max-width="518"
    />

    <!-- Edit Activity Modal (Reusing MakercartDetailModal in edit mode) -->
    <MakercartDetailModal
      :visible="showEditModal"
      :makercart-id="selectedActivity?.makercart_id"
      :edit-mode="true"
      :cart-id="currentCartId"
      :activity-data="selectedActivity"
      :cart-type="cartType"
      @close="closeEditModal"
      @saved="closeEditModal"
    />

    <!-- Signature Convert to Custom Modal -->
    <ConfirmationModal
      :width="600"
      v-model="showSignatureConvertModal"
      title="Confirm Edit"
      description="If you edit the Signature Makercart, the price will switch to the Custom Makercart price. Would you like to proceed?"
      confirm-text="Edit Makercart"
      cancel-text="Keep Signature Makercart"
      :loading="isCartLoading"
      @confirm="handleSignatureConvertConfirm"
      @cancel="handleSignatureConvertCancel"
    />

    <!-- Mobile Bottom Sheet -->
    <v-bottom-sheet
      v-model="showBottomSheet"
      class="activity-bottom-sheet"
      v-if="isMobile"
    >
      <v-card class="activity-bottom-sheet-card">
        <div class="activity-bottom-sheet-content">
          <!-- Drag Handle -->
          <div class="activity-bottom-sheet-handle"></div>

          <!-- Duplicate Activity -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('duplicate')"
          >
            <div class="activity-action-button">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 16H6C4.89543 16 4 15.1046 4 14V6C4 4.89543 4.89543 4 6 4H14C15.1046 4 16 4.89543 16 6V8M10 20H18C19.1046 20 20 19.1046 20 18V10C20 8.89543 19.1046 8 18 8H10C8.89543 8 8 8.89543 8 10V18C8 19.1046 8.89543 20 10 20Z"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Duplicate Activity</span>
            </div>
          </div>

          <!-- Move to another Cart -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('move')"
            v-if="locale !== 'SG'"
          >
            <div class="activity-action-button">
              <svg
                width="20"
                height="11"
                viewBox="0 0 20 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 1.49756L19 5.49756M19 5.49756L15 9.49756M19 5.49756L1 5.49756"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Move to another Cart</span>
            </div>
          </div>

          <!-- Edit Activity -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('edit')"
          >
            <div class="activity-action-button">
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.2322 3.72979L16.7677 7.26533M14.7322 2.22979C15.7085 1.25348 17.2914 1.25348 18.2677 2.22979C19.244 3.2061 19.244 4.78901 18.2677 5.76533L4.5 19.533H1V15.962L14.7322 2.22979Z"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Edit Activity</span>
            </div>
          </div>

          <!-- Convert to Single Activity -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('convert')"
            v-if="locale !== 'SG'"
          >
            <div class="activity-action-button">
              <svg
                width="18"
                height="21"
                viewBox="0 0 18 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5.49756V15.4976C1 17.7067 4.58172 19.4976 9 19.4976C13.4183 19.4976 17 17.7067 17 15.4976V5.49756M1 5.49756C1 7.7067 4.58172 9.49756 9 9.49756C13.4183 9.49756 17 7.7067 17 5.49756M1 5.49756C1 3.28842 4.58172 1.49756 9 1.49756C13.4183 1.49756 17 3.28842 17 5.49756M17 10.4976C17 12.7067 13.4183 14.4976 9 14.4976C4.58172 14.4976 1 12.7067 1 10.4976"
                  stroke="#3B4648"
                  stroke-width="2"
                />
              </svg>
              <span>Convert to Single Activity</span>
            </div>
          </div>

          <!-- Delete Activity -->
          <div
            class="activity-bottom-sheet-item"
            @click="handleBottomSheetAction('delete')"
          >
            <div class="activity-action-button">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 6H5H21"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 11V17"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14 11V17"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Delete Activity</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script setup>
import { useCart } from "@/composeables/useCart";
import { useLocaleStore } from "@/store/locale";
import {
  defineEmits,
  defineProps,
  inject,
  ref,
  computed,
  onMounted,
  onUnmounted,
} from "vue";
import { useRouter } from "vue-router";
import ActionButton from "../common/ActionButton.vue";
import MoveActivityModal from "./carts/MoveActivityModal.vue";
import DuplicateModal from "./carts/DuplicateModal.vue";
import DeleteModal from "./carts/DeleteModal.vue";
import ConfirmationModal from "./carts/ConfirmationModal.vue";
import MakercartDetailModal from "./catalog/custom/CustomMakercartDetailModal.vue";

const localeStore = useLocaleStore();
const locale = computed(() => localeStore.currentLocale);

const props = defineProps({
  activities: {
    type: Array,
    default: () => [],
  },
  maxActivities: {
    type: Number,
    default: 8,
  },
  currentCartId: {
    type: [String, Number],
    default: null,
  },
  availableCarts: {
    type: Array,
    default: () => [],
  },
  cartType: {
    type: String,
    default: "custom",
  },
});

const router = useRouter();
const {
  moveActivityBetweenCarts,
  duplicateActivityInCart,
  deleteActivityFromCart,
  convertActivityToSingle,
  convertSignatureToCustom,
  quoteGenerated,
  isCartLoading,
} = useCart();
const notify = inject("notify");

const emit = defineEmits([
  "edit-activity",
  "remove-activity",
  "activity-moved",
  "activity-duplicated",
  "activity-deleted",
  "activity-converted",
]);

// Modal state
const showMoveModal = ref(false);
const showDuplicateModal = ref(false);
const showDeleteModal = ref(false);
const showConvertModal = ref(false);
const showSignatureConvertModal = ref(false);
const selectedActivity = ref(null);
const selectedActivityIndex = ref(null);
const isCartFull = ref(false);
const showEditModal = ref(false);
const isMobile = ref(false);
const showBottomSheet = ref(false);
const bottomSheetActivity = ref(null);
const bottomSheetActivityIndex = ref(null);

// Computed properties for duplicate modal content
const duplicateModalTitle = ref("Duplicate Activity");
const duplicateModalDescription = computed(() => {
  return isCartFull.value
    ? "Your current cart is full. This activity will be duplicated into a new cart."
    : "This will create a copy of the activity in the same cart. Are you sure you want to duplicate it?";
});

const duplicateModalConfirmText = ref("Duplicate Activity");

const formatGrade = (grade) => {
  if (Array.isArray(grade)) {
    return grade.join(" / ");
  }
  return grade || "-";
};

const addActivityToCart = () => {
  router.push({
    name: "MakerCartPage",
  });
};

const editActivity = (activity, index) => {
  // For signature cart type, show conversion confirmation modal
  if (props.cartType === "signature") {
    selectedActivity.value = activity;
    selectedActivityIndex.value = index;
    showSignatureConvertModal.value = true;
  } else {
    emit("edit-activity", { activity, index });
  }
};

const openEditModal = (activity, index) => {
  selectedActivity.value = activity;
  selectedActivityIndex.value = index;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
};

const getAddActivityPosition = (activityCount) => {
  if (activityCount === 0) {
    return "position-center";
  }

  if (activityCount >= props.maxActivities) {
    return "position-hidden";
  }

  return "";
};

const openMoveModal = (activity, index) => {
  selectedActivity.value = activity;
  selectedActivityIndex.value = index;
  showMoveModal.value = true;
};

const closeMoveModal = () => {
  showMoveModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
};

const handleMoveActivity = async (moveData) => {
  try {
    const { targetCartId, activityData } = moveData;
    const result = await moveActivityBetweenCarts(
      props.currentCartId,
      targetCartId,
      activityData
    );

    if (result.success) {
      emit("activity-moved", {
        activity: activityData,
        index: selectedActivityIndex.value,
      });
      notify.plain("Activity moved successfully");

      closeMoveModal();
    } else {
      console.error("Failed to move activity:", result.error);
      notify.error(result.error);
    }
  } catch (error) {
    console.error("Error moving activity:", error);
    notify.error("Failed to move activity");
  }
};

const openDuplicateModal = (activity, index) => {
  selectedActivity.value = activity;
  selectedActivityIndex.value = index;

  // Check if current cart is full
  isCartFull.value = props.activities.length >= props.maxActivities;

  showDuplicateModal.value = true;
};

const openDeleteModal = (activity, index) => {
  selectedActivity.value = activity;
  selectedActivityIndex.value = index;
  showDeleteModal.value = true;
};

const handleDuplicateConfirm = async () => {
  try {
    const result = await duplicateActivityInCart(
      props.currentCartId,
      selectedActivity.value,
      props.cartType
    );

    if (result.success) {
      emit("activity-duplicated", {
        activity: selectedActivity.value,
        cartId: result.cart.id,
        index: selectedActivityIndex.value,
        isNewCart: result.isNewCart,
      });
      notify.plain(result.message);
      showDuplicateModal.value = false;
    } else {
      console.error("Failed to duplicate activity:", result.error);
      notify.error(result.error);
    }
  } catch (error) {
    console.error("Error duplicating activity:", error);
    notify.error("Failed to duplicate activity");
  }
};

const handleDuplicateCancel = () => {
  showDuplicateModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
  isCartFull.value = false;
};

const handleDeleteConfirm = async () => {
  try {
    const result = await deleteActivityFromCart(
      props.currentCartId,
      selectedActivity.value.entry_id
    );

    if (result.success) {
      emit("activity-deleted", {
        activity: selectedActivity.value,
        cartId: props.currentCartId,
        index: selectedActivityIndex.value,
      });
      notify.plain("Activity deleted successfully");
      showDeleteModal.value = false;
    } else {
      console.error("Failed to delete activity:", result.error);
      notify.error(result.error);
    }
  } catch (error) {
    console.error("Error deleting activity:", error);
    notify.error("Failed to delete activity");
  }
};

const handleDeleteCancel = () => {
  showDeleteModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
};

const openConvertModal = (activity, index) => {
  selectedActivity.value = activity;
  selectedActivityIndex.value = index;
  showConvertModal.value = true;
};

const handleConvertConfirm = async () => {
  try {
    const result = await convertActivityToSingle(
      props.currentCartId,
      selectedActivity.value,
      props.cartType
    );

    if (result.success) {
      emit("activity-converted", {
        activity: selectedActivity.value,
        cartId: props.currentCartId,
        index: selectedActivityIndex.value,
        singleActivity: result.singleActivity,
      });
      notify.plain("Activity converted to single activity successfully");
      showConvertModal.value = false;
    } else {
      console.error("Failed to convert activity:", result.error);
      notify.error(result.error);
    }
  } catch (error) {
    console.error("Error converting activity:", error);
    notify.error("Failed to convert activity");
  }
};

const handleConvertCancel = () => {
  showConvertModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
};

const handleSignatureConvertConfirm = async () => {
  try {
    const result = await convertSignatureToCustom(props.currentCartId);

    if (result.success) {
      emit("activity-converted", {
        activity: selectedActivity.value,
        cartId: props.currentCartId,
        index: selectedActivityIndex.value,
        convertedToCustom: true,
      });
      notify.plain("Signature makercart converted to custom successfully");
      showSignatureConvertModal.value = false;
    } else {
      console.error("Failed to convert signature makercart:", result.error);
      notify.error(result.error);
    }
  } catch (error) {
    console.error("Error converting signature makercart:", error);
    notify.error("Failed to convert signature makercart");
  }
};

const handleSignatureConvertCancel = () => {
  showSignatureConvertModal.value = false;
  selectedActivity.value = null;
  selectedActivityIndex.value = null;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const openBottomSheet = (activity, index) => {
  bottomSheetActivity.value = activity;
  bottomSheetActivityIndex.value = index;
  showBottomSheet.value = true;
};

const closeBottomSheet = () => {
  showBottomSheet.value = false;
  bottomSheetActivity.value = null;
  bottomSheetActivityIndex.value = null;
};

const handleBottomSheetAction = (action) => {
  const activity = bottomSheetActivity.value;
  const index = bottomSheetActivityIndex.value;

  closeBottomSheet();

  switch (action) {
    case "duplicate":
      openDuplicateModal(activity, index);
      break;
    case "move":
      openMoveModal(activity, index);
      break;
    case "edit":
      openEditModal(activity, index);
      break;
    case "convert":
      openConvertModal(activity, index);
      break;
    case "delete":
      openDeleteModal(activity, index);
      break;
  }
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>

<style scoped>
.activity-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  padding: 24px;
}

.activity-container {
  background-color: var(--white);
  border-radius: 24px;
  padding: 16px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: fit-content;
}

.activity-header {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.activity-title-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.activity-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
}

.add-activity-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 48px;
  height: 96px;
  border: none;
  background-color: transparent;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.add-activity-btn:hover {
  transform: translateY(-2px);
}

.add-activity-text {
  font-family: "Inter", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
}

.add-activity-icon {
  width: 42.67px;
  height: 42.67px;
}

.add-activity-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: var(--neutral-700);
  text-align: center;
  margin: 0;
}

.drag-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: none;
  align-items: center;
  justify-content: center;
}

.drag-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.activity-divider {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
  margin-top: 8px;
}

.activity-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.detail-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.detail-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: var(--neutral-700);
  margin-right: 8px;
  flex-shrink: 0;
}

.detail-value {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: var(--black-body-text);
  flex: 1;
}

.poster-row--no .detail-value {
  color: var(--neutral-700);
}

.edit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 10px 16px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.edit-button:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.edit-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.edit-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  color: var(--primary-blue);
}

/* Add Activity Container Positioning */
.add-activity-container {
  border: 2px dashed var(--border-dashed);
  border-radius: 16px;
  padding: 30px;
  height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background-color: var(--white);
  cursor: pointer;
}

.add-activity-container.position-center {
  grid-column: 1 / -1;
  grid-row: 1;
}

.add-activity-container.position-right {
  grid-column: 2;
  grid-row: 1;
}

/* Activity Menu List Styles */
.activity-menu-list {
  background: white;
  border-radius: 12px !important;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  padding: 8px;
  min-width: 262px;
}

.activity-menu-item {
  margin-bottom: 2px;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.activity-menu-item:last-child {
  margin-bottom: 0;
}

.activity-menu-item:hover {
  background-color: var(--white-hover);
}

.activity-menu-item.active {
  background-color: var(--primary-blue);
  color: white;
}

.activity-menu-item.active .activity-action-button {
  color: white;
}

.activity-menu-item.active .activity-action-button svg {
  stroke: white;
}

.activity-action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px !important;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--black-body-text);
  width: 100%;
  height: 48px;
  box-sizing: border-box;
}

.activity-action-button:hover {
  background-color: transparent;
}

.activity-action-button svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  stroke: var(--black-body-text);
}

.add-activity-container.position-bottom-left {
  grid-column: 1;
  grid-row: 2;
}

.add-activity-container.position-bottom-right {
  grid-column: 2;
  grid-row: 2;
}

.add-activity-container.position-hidden {
  display: none;
}

.activity-action-button {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .activity-list {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 2px;
  }

  .activity-container {
    width: 100%;
    max-width: 100%;
    padding: 12px;
  }

  .activity-title {
    font-size: var(--fs-medium);
    line-height: 24px;
    white-space: normal;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .detail-label,
  .detail-value {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .activity-divider {
    margin-top: 0;
  }

  .drag-icon {
    display: flex;
  }

  :deep(.activity-edit-btn.action-button) {
    min-height: 40px;
    border-radius: 8px;
    font-size: var(--fs-small);
    line-height: 20px;
    padding: 10px 16px;
  }

  :deep(.activity-edit-btn .action-button__icon svg),
  :deep(.activity-edit-btn .action-button__icon img) {
    width: 16px;
    height: 16px;
  }
  .edit-button {
    height: 44px;
    padding: 8px 12px;
  }

  .edit-text {
    font-size: var(--fs-small);
  }

  .add-activity-text {
    font-size: var(--fs-medium);
  }
}

@media (max-width: 480px) {
  .activity-container {
    padding: 12px;
  }

  .activity-title {
    font-size: var(--fs-medium);
    line-height: 24px;
  }

  .detail-label,
  .detail-value {
    font-size: 13px;
    line-height: 18px;
  }
}

/* Mobile Bottom Sheet Styles */
.activity-bottom-sheet {
  z-index: 10000001;
}

.activity-bottom-sheet-card {
  border-radius: 28px 28px 0 0 !important;
  overflow: hidden;
  background-color: #fff;
}

.activity-bottom-sheet-handle {
  width: 16px;
  height: 4px;
  background-color: #d8dae5;
  border-radius: 2px;
  margin: 12px auto 0;
  flex-shrink: 0;
}

.activity-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.activity-bottom-sheet-item {
  width: 100%;
  cursor: pointer;
}

.activity-bottom-sheet-item .activity-action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 48px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.2s ease;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  width: 100%;
  box-sizing: border-box;
}

.activity-bottom-sheet-item .activity-action-button:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.activity-bottom-sheet-item .activity-action-button svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  stroke: #3b4648;
}

.activity-bottom-sheet-item .activity-action-button span {
  flex: 1;
  text-align: left;
}

@media (max-width: 768px) {
  .activity-bottom-sheet-content {
    padding: 12px 16px 24px;
  }
}
</style>
